import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getProcurementUsers } from '../../../shared/api/users.api'
import { getProcurementNotesBulk } from '../../../shared/api/procurementNotes.api'
import { getExchangeRates, getMarkupConstants } from '../../../shared/api/exchangeRates.api'
import type { Request } from '../../../shared/api/types'

/** Готовность ценовых справочников: курс CNY + коэффициенты наценки.
 *  Пока `ready === false`, считать рубли и выставлять КП нельзя. */
export type PricingReadiness = {
  ready: boolean
  isLoading: boolean
  isError: boolean
  retry: () => void
}

/** `loadProcNotes` — грузить заметки закупщиков: колонка заметки есть только у
 *  самого закупщика, остальным ролям запрос не нужен. */
export function useRequestRelatedData(request: Request | undefined, loadProcNotes: boolean) {
  const { data: procUsers = [] } = useQuery({
    queryKey: ['procurement-users'],
    queryFn: getProcurementUsers,
  })

  const itemIds = useMemo(() => (request?.items ?? []).map((i) => i.id), [request?.items])

  const { data: procNotes = [] } = useQuery({
    queryKey: ['procurement-table-notes', itemIds],
    queryFn: () => getProcurementNotesBulk(itemIds),
    enabled: loadProcNotes && itemIds.length > 0,
  })

  const ratesQuery = useQuery({
    queryKey: ['exchange-rates'],
    queryFn: getExchangeRates,
    staleTime: 5 * 60 * 1000,
  })

  const markupQuery = useQuery({
    queryKey: ['markup-constants'],
    queryFn: getMarkupConstants,
    staleTime: 5 * 60 * 1000,
  })

  const exchangeRates = ratesQuery.data
  const markupConstants = markupQuery.data

  // Проверка `> 0` обязательна: `?? null` пропустил бы 0 из БД, а нулевой курс
  // обнулит все не-РФ цены так же тихо, как раньше это делал фолбэк-курс 1.
  const rawCny = exchangeRates?.CNY
  const cnyRate = typeof rawCny === 'number' && rawCny > 0 ? rawCny : null

  const pricing: PricingReadiness = {
    ready: cnyRate !== null && !!markupConstants,
    isLoading: ratesQuery.isLoading || markupQuery.isLoading,
    isError: ratesQuery.isError || markupQuery.isError,
    retry: () => {
      void ratesQuery.refetch()
      void markupQuery.refetch()
    },
  }

  const notesMap = useMemo(() => {
    const m = new Map<string, Map<string, string>>()
    for (const n of procNotes) {
      if (!m.has(n.requestItemId)) m.set(n.requestItemId, new Map())
      m.get(n.requestItemId)!.set(n.procurementUserId, n.note ?? '')
    }
    return m
  }, [procNotes])

  return { procUsers, notesMap, exchangeRates, markupConstants, cnyRate, pricing }
}
